// components/OccasionsModal.jsx
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    Stack,
    Typography,
    IconButton,
    Chip,
    Divider,
    TextField,
    Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

function ensureArray(v) {
    return Array.isArray(v) ? v : [];
}

const whiteInput = {
    sx: {
        "& .MuiOutlinedInput-root": {
            color: "#fff",
            "& fieldset": { borderColor: "rgba(255,255,255,0.30)" },
            "&:hover fieldset": { borderColor: "rgba(255,255,255,0.55)" },
            "&.Mui-focused fieldset": { borderColor: "#fff" },
        },
        "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.85)" },
    },
};

export default function OccasionsModal({
    open,
    onClose,

    // options: [{ value: "Wedding", nameEn: "Wedding", nameGr: "Γάμος" }, ...]
    options = [],

    // lang: current language ('gr' | 'en')
    lang = "gr",

    // selected: ["Wedding","Party"]  — array of English value strings
    selected = [],

    // setSelected: (arr: string[]) => void
    setSelected,

    // addCustomOption: ({ nameEn, nameGr }) => void  — called when user adds a new occasion
    addCustomOption,
}) {
    const { t } = useTranslation();
    const [newEn, setNewEn] = useState("");
    const [newGr, setNewGr] = useState("");

    const selectedArr = ensureArray(selected).map(String);

    const normalizedOptions = useMemo(() => {
        const map = new Map();

        ensureArray(options).forEach((o) => {
            const value = String(o?.value ?? o?.nameEn ?? o);
            const nameEn = String(o?.nameEn ?? o?.value ?? o);
            const nameGr = String(o?.nameGr ?? o?.nameEn ?? o?.value ?? o);
            const label = lang === "gr" ? (nameGr || nameEn) : (nameEn || nameGr);
            map.set(value, { value, label, nameEn, nameGr });
        });

        // ensure selected values always visible even if not in options
        selectedArr.forEach((v) => {
            if (!map.has(v)) map.set(v, { value: v, label: v, nameEn: v, nameGr: v });
        });

        return Array.from(map.values());
    }, [options, selected, lang]);

    const toggle = (value) => {
        const v = String(value);
        if (selectedArr.includes(v)) {
            setSelected(selectedArr.filter((x) => x !== v));
        } else {
            setSelected([...selectedArr, v]);
        }
    };

    const addNew = () => {
        const en = newEn.trim();
        const gr = newGr.trim();
        if (!en || !gr) return;

        const exists = normalizedOptions.some(
            (o) => String(o.nameEn).toLowerCase() === en.toLowerCase()
        );
        if (exists) {
            const found = normalizedOptions.find(
                (o) => String(o.nameEn).toLowerCase() === en.toLowerCase()
            );
            if (found && !selectedArr.includes(found.value)) {
                setSelected([...selectedArr, found.value]);
            }
            setNewEn("");
            setNewGr("");
            return;
        }

        addCustomOption?.({ nameEn: en, nameGr: gr });
        setNewEn("");
        setNewGr("");
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ bgcolor: "rgba(0,0,0,0.85)", color: "#fff" }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography fontWeight={900}>{t("modals.occasions.title")}</Typography>
                    <IconButton onClick={onClose} sx={{ color: "#fff" }}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ bgcolor: "rgba(0,0,0,0.85)", color: "#fff" }}>
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)", mb: 1 }}>
                    {t("modals.occasions.instruction")}
                </Typography>

                <Box
                    mb={2}
                    mt={2}
                    p={2}
                    minHeight={200}
                    maxHeight={350}
                    overflow="auto"
                    sx={{ border: "1px solid rgba(255,255,255,0.12)", borderRadius: 2 }}
                >
                    {normalizedOptions.length > 0 ? (
                        <Box display="flex" flexWrap="wrap" gap={1.5}>
                            {normalizedOptions.map((o) => {
                                const active = selectedArr.includes(String(o.value));
                                return (
                                    <Chip
                                        key={o.value}
                                        label={o.label}
                                        onClick={() => toggle(o.value)}
                                        sx={{
                                            cursor: "pointer",
                                            fontSize: "0.875rem",
                                            padding: "3px 8px",
                                            height: "auto",
                                            transition: "all 0.2s",
                                            color: "#fff !important",
                                            bgcolor: active ? "rgba(255,255,255,0.42)" : "rgba(255,255,255,0.10)",
                                            border: "1px solid",
                                            borderColor: active ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.35)",
                                            boxShadow: active ? "0 0 0 1px rgba(255,255,255,0.25) inset" : "none",
                                            "& .MuiChip-label": { color: "#fff !important" },
                                            "&:hover": {
                                                transform: "scale(1.04)",
                                                bgcolor: active ? "rgba(255,255,255,0.55)" : "rgba(255,255,255,0.18)",
                                                borderColor: active ? "#fff" : "rgba(255,255,255,0.55)",
                                            },
                                        }}
                                    />
                                );
                            })}
                        </Box>
                    ) : (
                        <Typography variant="body2" textAlign="center" py={4} sx={{ color: "rgba(255,255,255,0.75)" }}>
                            {t("modals.occasions.noOptions")}
                        </Typography>
                    )}
                </Box>

                <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.25)" }} />

                <Typography fontWeight={900} sx={{ mb: 1.5 }}>
                    {t("modals.occasions.addTitle")}
                </Typography>

                <Stack spacing={1.5}>
                    <TextField
                        value={newGr}
                        onChange={(e) => setNewGr(e.target.value)}
                        label={t("modals.occasions.labelGr")}
                        placeholder={t("modals.occasions.placeholderGr")}
                        fullWidth
                        {...whiteInput}
                        onKeyDown={(e) => { if (e.key === "Enter") addNew(); }}
                    />
                    <TextField
                        value={newEn}
                        onChange={(e) => setNewEn(e.target.value)}
                        label={t("modals.occasions.labelEn")}
                        placeholder={t("modals.occasions.placeholderEn")}
                        fullWidth
                        {...whiteInput}
                        onKeyDown={(e) => { if (e.key === "Enter") addNew(); }}
                    />
                    <Button
                        variant="contained"
                        onClick={addNew}
                        disabled={!newEn.trim() || !newGr.trim()}
                        sx={{ alignSelf: "flex-start", minWidth: 140 }}
                    >
                        {t("modals.occasions.addBtn")}
                    </Button>
                </Stack>

                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                    <Button onClick={onClose} sx={{ color: "#fff" }}>
                        {t("common.close")}
                    </Button>
                </Stack>
            </DialogContent>
        </Dialog>
    );
}
